import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, BookOpen } from 'lucide-react';
import { ContactFormModal } from './ContactFormModal';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hide header on admin pages
  if (location.pathname.startsWith('/admin')) return null;

  const transparent = isHome && !scrolled;

  const isActive = (path) => location.pathname === path;
  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/business', label: 'For Business' },
    { path: '/personal', label: 'Personal Growth' },
    { path: '/events', label: 'Events' },
  ];

  return (
    <>
      <ContactFormModal isOpen={showForm} onClose={() => setShowForm(false)} inquiryType="booking_call" sourcePage="header" />
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${transparent ? 'bg-transparent' : 'bg-[#FFFBF8] shadow-sm'}`} style={transparent ? {} : { borderBottom: '1px solid #F0E6F6' }}>
        <div className="container-padding">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="relative z-10" data-testid="header-logo">
              <img src="/images/Logo/Natasha Simone website (4).png" alt="Natasha Simone" className={`h-16 md:h-20 w-auto transition-all duration-300 ${transparent ? 'brightness-0 invert' : ''}`} />
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className={`text-sm tracking-wide font-medium transition-colors duration-300 cta-underline ${transparent ? (isActive(link.path) ? 'text-white' : 'text-white/80 hover:text-white') : `nav-link ${isActive(link.path) ? 'nav-link-active' : ''}`}`} data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}>
                  {link.label}
                </Link>
              ))}
              <a
                href="https://buy.stripe.com/5kQ6oIaPx9G5f1O2jigjC00"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium tracking-wider uppercase rounded-full border transition-all duration-300 ${transparent ? 'bg-white/15 text-white border-white/30 hover:bg-white hover:text-[#D13A7A] hover:border-white' : 'bg-[#FFE8F0] text-[#D13A7A] border-[#E84993]/20 hover:bg-[#E84993] hover:text-white hover:border-[#E84993]'}`}
                data-testid="nav-buy-book"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Buy the Book
              </a>
            </nav>
            <button
              onClick={() => setShowForm(true)}
              className={`hidden md:inline-flex items-center gap-2 px-6 py-2.5 text-xs font-medium tracking-wider uppercase rounded-full transition-all duration-350 hover:-translate-y-0.5 ${transparent ? 'bg-white text-[#E84993] hover:shadow-lg' : 'text-white'}`}
              style={transparent ? {} : { background: 'linear-gradient(135deg, #E84993 0%, #D13A7A 100%)' }}
              data-testid="header-book-call"
            >
              Book a Call
            </button>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden relative z-10 p-2" data-testid="mobile-menu-toggle" aria-label="Toggle menu">
              {isMenuOpen ? <X className="w-6 h-6 text-[#1A1A1A]" /> : <Menu className={`w-6 h-6 ${transparent ? 'text-white' : 'text-[#1A1A1A]'}`} />}
            </button>
          </div>
        </div>
        {isMenuOpen && (
          <div className="md:hidden fixed inset-x-0 bottom-0 bg-[#FFFBF8] z-50" style={{ top: '96px' }}>
            <nav className="flex flex-col items-center pt-16 gap-8 bg-[#FFFBF8]">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} onClick={() => setIsMenuOpen(false)} className={`text-xl font-medium tracking-wide ${isActive(link.path) ? 'text-[#1A1A1A]' : 'text-[#7A7A7A]'}`} data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}>
                  {link.label}
                </Link>
              ))}
              <a href="https://buy.stripe.com/5kQ6oIaPx9G5f1O2jigjC00" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)} className="inline-flex items-center gap-2 text-xl font-medium tracking-wide text-[#E84993]" data-testid="mobile-nav-buy-book">
                <BookOpen className="w-5 h-5" /> Buy the Book
              </a>
              <button onClick={() => { setIsMenuOpen(false); setShowForm(true); }} className="mt-4 btn-primary arrow-hover" data-testid="mobile-book-call">
                Book a Call <ArrowRight className="w-4 h-4" />
              </button>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
